import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Form from 'components/Form';
import TextField from 'components/TextField';

import { useErrorHandler } from 'lib';
import { GoogleSettings_data$key } from '__generated__/GoogleSettings_data.graphql';
import { GoogleSettings_settings$key } from '__generated__/GoogleSettings_settings.graphql';
import { GoogleSettingsMutation } from '__generated__/GoogleSettingsMutation.graphql';
import GoogleCredentials from 'components/GoogleCredentials';

type FormFields = {
  analyticsId: string;
  apiKey: string;
  loginClientId: string;
  loginClientSecret: string;
};

type Props = {
  data: GoogleSettings_data$key;
  settings: GoogleSettings_settings$key;
};

const GoogleSettings: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { google } = useFragment(
    graphql`
      fragment GoogleSettings_settings on Settings {
        google {
          analyticsId
          apiKey
          loginClientId
          loginClientSecret
        }
      }
    `,
    props.settings
  );

  const data = useFragment(
    graphql`
      fragment GoogleSettings_data on Query {
        ...GoogleCredentials_data
      }
    `,
    props.data
  );

  const [update, updating] = useMutation<GoogleSettingsMutation>(graphql`
    mutation GoogleSettingsMutation($input: UpdateSettingsInput!) {
      updateSettings(input: $input) {
        ...GoogleSettings_settings
      }
    }
  `);

  const initial = {
    analyticsId: google?.analyticsId ?? '',
    apiKey: google?.apiKey ?? '',
    loginClientId: google?.loginClientId ?? '',
    loginClientSecret: google?.loginClientSecret ?? '',
  };

  const handleSubmit = useCallback(
    (google: FormFields) => {
      update({
        variables: {
          input: { google },
        },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <TextField
              fullWidth
              name="analyticsId"
              label="Analytics Tracking ID"
            />

            <TextField fullWidth name="apiKey" label="Maps API Key" />
            <TextField fullWidth name="loginClientId" label="Login Client ID" />

            <TextField
              fullWidth
              name="loginClientSecret"
              label="Login Client Secret"
            />

            <GoogleCredentials data={data} />
          </Stack>
        </Paper>
      </Form>
    </Formik>
  );
};

export default GoogleSettings;
